import { useCallback, useMemo, useState } from "react";
import { Link, getRouteApi, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Plus, Search, Trash2, ListTree } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { ProjectIcon } from "@/components/ProjectIcon";
import { ProjectEmptyState, ProjectToolbar, ProjectStatusTabs } from "@/components/ProjectLayout";
import { IssueBoard } from "@/components/IssueBoard";
import { IssueCreateDialog, type IssueDefaults } from "@/components/IssueCreateDialog";
import { IssueList, type RowPickers } from "@/components/IssueList";
import { FilterChips, ViewControls } from "@/components/ViewControls";
import { Picker } from "@/components/Picker";
import { PriorityIcon, StateIcon, Avatar } from "@/components/PropertyIcons";
import { applyView, groupIssues, groupIssueTrees, PRIORITIES, type Group } from "@/lib/issues";
import { deleteIssue, liveIssues, liveStates, patchIssue, restoreIssue, useProject, useProjectStore } from "@/lib/store";
import { useShortcuts } from "@/lib/shortcuts";
import { ISSUE_STATUSES, matchesIssueTab } from "@/lib/workflow";
import { toast } from "@/lib/toast";
import { errorMessage } from "@/lib/api";
import { useView, useViews } from "@/lib/views";
import { ProjectLoading, useLoadProject } from "./ProjectHome";

const route = getRouteApi("/$ws/project/$key/issues/$tab");
const TABS = ["all", ...ISSUE_STATUSES] as const;
type Tab = (typeof TABS)[number];

export function ProjectIssuesPage() {
  const { ws, key, tab: rawTab } = route.useParams();
  const tab: Tab = TABS.includes(rawTab as Tab) ? (rawTab as Tab) : "all";
  // Remount per project/tab so selection and focus reset naturally.
  return <ProjectIssuesView key={`${ws}/${key}/${tab}`} ws={ws} kkey={key} tab={tab} />;
}

function ProjectIssuesView({ ws, kkey: key, tab }: { ws: string; kkey: string; tab: Tab }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  useLoadProject(ws, key);
  const d = useProject(ws, key);
  const viewKey = `project:${key}:issues:${tab}`;
  const view = useView(viewKey);
  const setView = useViews((s) => s.set);

  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [rowPickers, setRowPickers] = useState<RowPickers>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createDefaults, setCreateDefaults] = useState<IssueDefaults | undefined>();

  const issues = useMemo(() => (d ? applyView(d, liveIssues(d), view, tab) : []), [d, view, tab]);
  const groups = useMemo(() => (d ? groupIssues(d, issues, view, t).filter((g) => !g.state || matchesIssueTab(g.state.type, tab)) : []), [d, issues, view, t, tab]);
  const treeGroups = useMemo(() => d ? groupIssueTrees(d, issues, view, t, collapsed).filter((group) => !group.state || matchesIssueTab(group.state.type, tab)) : [], [d, issues, view, t, collapsed, tab]);
  const flat = useMemo(() => view.layout === "board" ? groups.flatMap((g) => g.issues) : treeGroups.flatMap((g) => g.rows.map((node) => node.item)), [groups, treeGroups, view.layout]);

  const move = useCallback(
    (delta: number) => {
      if (flat.length === 0) return;
      const idx = flat.findIndex((i) => i.id === focusedId);
      const next = idx < 0 ? (delta > 0 ? 0 : flat.length - 1) : Math.max(0, Math.min(flat.length - 1, idx + delta));
      const id = flat[next]!.id;
      setFocusedId(id);
      document.querySelector(`[data-issue-id="${id}"]`)?.scrollIntoView({ block: "nearest" });
    },
    [flat, focusedId],
  );

  const openCreate = (defaults?: IssueDefaults) => {
    const state = d && tab !== "all" ? liveStates(d).find((s) => s.type === tab) : undefined;
    setCreateDefaults({ ...(state ? { state_id: state.id } : {}), ...defaults });
    setCreateOpen(true);
  };
  const createInGroup = (g: Group) => {
    const def: IssueDefaults = {};
    if (view.groupBy === "state") def.state_id = g.key;
    if (view.groupBy === "priority") def.priority = Number(g.key);
    if (view.groupBy === "assignee") def.assignee_id = g.key === "none" ? null : g.key;
    if (view.groupBy === "label" && g.key !== "none") def.label_ids = [g.key];
    openCreate(def);
  };

  const shortcuts = useMemo(
    () => [
      { keys: "c", run: () => openCreate() },
      { keys: "j", run: () => move(1) },
      { keys: "k", run: () => move(-1) },
      { keys: "ArrowDown", run: () => move(1) },
      { keys: "ArrowUp", run: () => move(-1) },
      { keys: "Enter", run: () => {
          const i = d && focusedId ? d.issues[focusedId] : undefined;
          if (i) void navigate({ to: "/$ws/issue/$identifier", params: { ws, identifier: `${d!.project.key}-${i.number}` } });
        } },
      { keys: "x", run: () => focusedId && toggleSelect(focusedId) },
      { keys: "s", run: () => focusedId && setRowPickers({ issueId: focusedId, open: "state" }) },
      { keys: "p", run: () => focusedId && setRowPickers({ issueId: focusedId, open: "priority" }) },
      { keys: "a", run: () => focusedId && setRowPickers({ issueId: focusedId, open: "assignee" }) },
      { keys: "l", run: () => focusedId && setRowPickers({ issueId: focusedId, open: "label" }) },
      { keys: "Escape", run: () => { setSelected(new Set()); setRowPickers(null); } },
      { keys: "Meta+b", run: () => setView(viewKey, { layout: view.layout === "list" ? "board" : "list" }) },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [move, focusedId, d, ws, navigate, view.layout, viewKey, tab],
  );
  useShortcuts(shortcuts);

  const toggleSelect = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  if (!d) return <ProjectLoading ws={ws} kkey={key} />;

  const total = liveIssues(d).length;
  const canEdit = !!d.project.role;

  return (
    <>
      <PageHeader crumbs={[<ProjectCrumb key="p" ws={ws} d={d} />, t("nav.issues")]} right={canEdit && <button className="btn btn-primary h-8 text-[13px]" onClick={() => openCreate()}><Plus className="h-3.5 w-3.5" />{t("issue.create")}</button>} />
      <ProjectToolbar right={<>
        {total > 0 && <label className="relative w-[160px]">
          <Search className="pointer-events-none absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2 text-faint" />
          <input className="input h-8 w-full border-transparent bg-transparent py-0 pl-8 text-[12px] hover:border-border"
            aria-label={t("issues.searchPlaceholder")} placeholder={t("issues.searchPlaceholder")} value={view.filters.text ?? ""}
            onChange={(e) => useViews.getState().setFilters(viewKey, { ...view.filters, text: e.target.value || undefined })} />
        </label>}
        {canEdit && total > 0 && view.layout === "list" && (view.orderBy !== "manual" || view.groupBy !== "none") && <button className="btn btn-ghost h-8 text-[12px]" title={t("tree.manualHelp")} onClick={() => setView(viewKey, { orderBy: "manual", groupBy: "none", showSubIssues: true })}><ListTree className="h-3.5 w-3.5" />{t("tree.manual")}</button>}
        <ViewControls d={d} viewKey={viewKey} view={view} />
      </>}>
        <ProjectStatusTabs ws={ws} projectKey={key} kind="issues" selected={tab} label={t("issue.status")} tabs={TABS.map((id) => ({ id, label: t(`issues.tabs.${id}`) }))} />
      </ProjectToolbar>
      <FilterChips d={d} view={view} viewKey={viewKey} />

      {total === 0 ? (
        <ProjectEmptyState
            title={t("issues.emptyTitle")}
            body={t("issues.emptyBody")}
            actions={
              <>
                {canEdit && (
                  <button className="btn btn-primary" onClick={() => openCreate()}>
                    {t("issues.createFirst")} <kbd className="kbd ml-1">C</kbd>
                  </button>
                )}
                <a className="btn" href="https://github.com/dbluklee/tern/blob/main/docs/features/issues.md" target="_blank" rel="noreferrer">
                  {t("common.documentation")}
                </a>
              </>
            }
          />
      ) : issues.length === 0 ? (
        <ProjectEmptyState title={tab === "all" ? t("issues.noMatchTitle") : t(`issues.tabs.${tab}`)} body={t("issues.noMatchBody")} actions={<>
          {canEdit && <button className="btn btn-primary" onClick={() => openCreate()}>{t("issue.create")}</button>}
          {Object.values(view.filters).some((value) => value?.length) && <button className="btn" onClick={() => useViews.getState().setFilters(viewKey, {})}>{t("view.clearFilters")}</button>}
        </>} />
      ) : view.layout === "board" ? (
        <IssueBoard d={d} groups={groups} view={view} onCreateInGroup={canEdit ? createInGroup : undefined} />
      ) : (
        <IssueList d={d} groups={treeGroups} collapsed={collapsed}
          onToggle={(id) => setCollapsed((current) => { const next = new Set(current); if (next.has(id)) next.delete(id); else next.add(id); return next; })}
          onExpand={(id) => setCollapsed((current) => { if (!current.has(id)) return current; const next = new Set(current); next.delete(id); return next; })} view={view} focusedId={focusedId} selected={selected} onFocus={setFocusedId} onToggleSelect={toggleSelect} rowPickers={rowPickers} onClosePicker={() => setRowPickers(null)} onCreateInGroup={canEdit ? createInGroup : undefined} />
      )}

      {selected.size > 0 && <BulkBar d={d} ids={[...selected]} onDone={() => setSelected(new Set())} />}
      <IssueCreateDialog d={d} open={createOpen} onOpenChange={setCreateOpen} defaults={createDefaults} />
    </>
  );
}

export function ProjectCrumb({ ws, d }: { ws: string; d: NonNullable<ReturnType<typeof useProject>> }) {
  return (
    <Link to="/$ws/project/$key" params={{ ws, key: d.project.key }} className="flex min-w-0 items-center gap-2 hover:text-fg" title={d.project.name}>
      <ProjectIcon project={d.project} size={16} />
      <span className="truncate">{d.project.name}</span>
    </Link>
  );
}

function BulkBar({ d, ids, onDone }: { d: NonNullable<ReturnType<typeof useProject>>; ids: string[]; onDone: () => void }) {
  const { t } = useTranslation();
  const issues = ids.map((id) => d.issues[id]).filter((i): i is NonNullable<typeof i> => !!i);
  const run = async (fn: (i: (typeof issues)[number]) => Promise<unknown>) => {
    try {
      await Promise.all(issues.map(fn));
      onDone();
    } catch (e) {
      toast(errorMessage(e), { tone: "error" });
    }
  };
  const remove = () =>
    void run(async (i) => deleteIssue(i)).then(() =>
      toast(t("issue.deletedN", { n: issues.length }), {
        action: { label: t("common.undo"), run: () => void Promise.all(issues.map((i) => restoreIssue(i))) },
      }),
    );
  return (
    <div className="pointer-events-auto fixed bottom-4 left-1/2 z-30 flex w-max max-w-[calc(100vw-32px)] -translate-x-1/2 flex-wrap items-center justify-center gap-1.5 rounded-xl border border-border-strong bg-elevated p-2 shadow-2xl">
      <span className="px-2 text-[12px] font-medium whitespace-nowrap text-fg">{t("issue.selectedN", { n: ids.length })}</span>
      <Picker value={null} items={liveStates(d).map((s) => ({ id: s.id, label: s.name, icon: <StateIcon state={s} /> }))} onSelect={(sid) => void run((i) => patchIssue(i, { state_id: sid }))} trigger={<button className="btn h-7 text-[12.5px]">{t("issue.status")}</button>} />
      <Picker value={null} items={PRIORITIES.map((p) => ({ id: String(p.value), label: t(`priority.${p.key}`), icon: <PriorityIcon priority={p.value} /> }))} onSelect={(v) => void run((i) => patchIssue(i, { priority: Number(v) }))} trigger={<button className="btn h-7 text-[12.5px]">{t("issue.priority")}</button>} />
      <Picker value={null} items={[{ id: "none", label: t("issue.unassigned"), icon: <Avatar size={16} /> }, ...Object.values(d.users).map((u) => ({ id: u.id, label: u.name, icon: <Avatar user={u} size={16} /> }))]} onSelect={(v) => void run((i) => patchIssue(i, { assignee_id: v === "none" ? null : v }))} trigger={<button className="btn h-7 text-[12.5px]">{t("issue.assignee")}</button>} />
      <button className="btn h-7 text-[12.5px] text-danger" onClick={remove}>
        <Trash2 className="h-3.5 w-3.5" /> {t("common.delete")}
      </button>
      <button className="btn btn-ghost h-7 text-[12.5px]" onClick={onDone}>
        {t("common.cancel")}
      </button>
    </div>
  );
}

export { useProjectStore };
